<template>
  <div>
    <b-card no-body>
      <b-card-body>
        <validation-observer ref="refFormObserver">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <h5 class="mb-0">{{ $t("dmp.menuDisplay.title") }}</h5>
            <div class="d-flex gap-1">
              <b-button v-if="!isTemplateMode" variant="primary" size="sm" @click="saveMenuDisplay" :disabled="isSaving">
                <b-spinner v-if="isSaving" small class="mr-25" />
                <feather-icon v-else icon="SaveIcon" size="14" class="mr-25" />
                {{ isSaving ? $t("actions.saving") : $t("actions.save") }}
              </b-button>
            </div>
          </div>

          <!-- Loading spinner -->
          <div v-if="isLoading" class="d-flex justify-content-center p-3">
            <b-spinner variant="primary" :label="$t('dmp.menuDisplay.loading')" />
            <span class="ml-2">{{ $t("dmp.menuDisplay.loading") }}...</span>
          </div>

          <b-form v-else @submit.prevent="saveMenuDisplay">
            <b-row>
              <b-col cols="12">
                <b-card class="mb-2 border">
                  <h6 class="mb-3 font-weight-bold">
                    {{ $t("dmp.menuDisplay.optionsSection") }}
                  </h6>

                  <b-row>
                    <!-- Armed Area Status Display -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.menuDisplay.armedAreaStatusDisplay')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.menuDisplay.armedAreaStatusDisplay')"
                          label-for="armed-area-status-display"
                        >
                          <b-form-input
                            id="armed-area-status-display"
                            v-model="menuDisplay.armedAreaStatusDisplay"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Time Display -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.menuDisplay.timeDisplay')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.menuDisplay.timeDisplay')"
                          label-for="time-display"
                        >
                          <b-form-input
                            id="time-display"
                            v-model="menuDisplay.timeDisplay"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <!-- Arm Disarm Display -->
                    <b-col cols="12" md="6" lg="4" class="mb-2">
                      <validation-provider
                        #default="validationContext"
                        :name="$t('dmp.menuDisplay.armDisarmDisplay')"
                        rules="required"
                      >
                        <b-form-group
                          :label="$t('dmp.menuDisplay.armDisarmDisplay')"
                          label-for="arm-disarm-display"
                        >
                          <b-form-input
                            id="arm-disarm-display"
                            v-model="menuDisplay.armDisarmDisplay"
                            :state="getValidationState(validationContext)"
                          />
                          <b-form-invalid-feedback>
                            {{ validationContext.errors[0] }}
                          </b-form-invalid-feedback>
                        </b-form-group>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, inject, getCurrentInstance, watch } from "@vue/composition-api";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useToast } from "vue-toastification/composition";
import formValidation from "@core/comp-functions/forms/form-validation";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useTemplateMode } from "./useTemplateMode";

export default {
  name: "DmpPanelsConfigMenuDisplay",
  components: {
    ValidationObserver,
    ValidationProvider,
    ToastificationContent,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
    menuDisplayData: {
      type: Object,
      default: () => ({}),
    },
    panelId: {
      type: [Number, String],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const toast = useToast();
    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;
    const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

    const { refFormObserver, getValidationState } = formValidation();

    const isSaving = ref(false);
    const isLoading = ref(false);

    const menuDisplay = ref({
      armDisarmDisplay:       "1-16",
      armedAreaStatusDisplay: "1-16",
      timeDisplay:            "1-16",
    });

    const loadMenuDisplay = () => {
      if (props.menuDisplayData && Object.keys(props.menuDisplayData).length > 0) {
        menuDisplay.value = {
          armDisarmDisplay:       props.menuDisplayData.armDisarmDisplay       ?? "1-16",
          armedAreaStatusDisplay: props.menuDisplayData.armedAreaStatusDisplay ?? "1-16",
          timeDisplay:            props.menuDisplayData.timeDisplay            ?? "1-16",
        };
      }
    };

    // Construye la sección en forma GET para el template.
    const buildTemplateItem = () => ({
      ...(props.menuDisplayData || {}),
      armDisarmDisplay:       menuDisplay.value.armDisarmDisplay,
      armedAreaStatusDisplay: menuDisplay.value.armedAreaStatusDisplay,
      timeDisplay:            menuDisplay.value.timeDisplay,
    });

    const validateTemplateSection = async () =>
      refFormObserver.value ? await refFormObserver.value.validate() : true;

    const saveMenuDisplay = async () => {
      const isValid = await refFormObserver.value.validate();
      if (!isValid) return;

      if (isTemplateMode.value) {
        emitTemplateUpdate("menu_display", buildTemplateItem());
        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.success"),
            text:    proxy.$t("dmp.menuDisplay.savedSuccessfully"),
            icon:    "CheckIcon",
            variant: "success",
          },
        });
        return;
      }

      isSaving.value = true;

      try {
        const payload = {
          armDisarmDisplay:       menuDisplay.value.armDisarmDisplay,
          armedAreaStatusDisplay: menuDisplay.value.armedAreaStatusDisplay,
          timeDisplay:            menuDisplay.value.timeDisplay,
        };

        await store.dispatch("dmp_panels/setMenuDisplay", {
          uuid: props.uuid,
          menuDisplayData: payload,
        });

        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.success"),
            text:    proxy.$t("dmp.menuDisplay.savedSuccessfully"),
            icon:    "CheckIcon",
            variant: "success",
          },
        });

        emit("operation-completed");
      } catch (error) {
        console.error("Error saving menu display:", error);
        emit("operation-error", error);
        toast({
          component: ToastificationContent,
          props: {
            title:   proxy.$t("dmp.common.error"),
            text:    proxy.$t("dmp.menuDisplay.errorSaving"),
            icon:    "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSaving.value = false;
      }
    };

    watch(
      () => props.menuDisplayData,
      (newData) => {
        if (newData && Object.keys(newData).length > 0) {
          loadMenuDisplay();
        }
      },
      { deep: true }
    );

    const registerTemplateSection = inject("registerTemplateSection", null);
    const unregisterTemplateSection = inject("unregisterTemplateSection", null);

    onMounted(() => {
      loadMenuDisplay();
      if (isTemplateMode.value && registerTemplateSection) {
        registerTemplateSection("menu_display", {
          validate: validateTemplateSection,
          collect: buildTemplateItem,
        });
      }
    });

    onBeforeUnmount(() => {
      if (isTemplateMode.value && unregisterTemplateSection) {
        unregisterTemplateSection("menu_display");
      }
    });

    return {
      refFormObserver,
      getValidationState,
      isSaving,
      isLoading,
      menuDisplay,
      saveMenuDisplay,
      isTemplateMode,
    };
  },
};
</script>

<style lang="scss" scoped>
.border {
  border: 1px solid #ebe9f1 !important;
}

.dark-layout {
  .border {
    border-color: #404656 !important;
  }
}
</style>
